import { supabase } from '@/integrations/supabase/client';

// Service pour l'intégration avec l'API Steam
// Documentation: https://developer.valvesoftware.com/wiki/Steam_Web_API

const STEAM_API_BASE = 'https://api.steampowered.com';
const STEAM_STORE_API_BASE = 'https://store.steampowered.com/api';

export interface SteamGame {
  appid: number;
  name: string;
  playtime_forever: number; // en minutes
  playtime_windows_forever: number;
  playtime_mac_forever: number;
  playtime_linux_forever: number;
  playtime_disconnected: number;
  rtime_last_played: number; // timestamp Unix
  playtime_2weeks?: number;
}

export interface SteamPlayerSummary {
  steamid: string;
  communityvisibilitystate: number;
  profilestate: number;
  personaname: string;
  profileurl: string;
  avatar: string;
  avatarmedium: string;
  avatarfull: string;
  personastate: number;
  realname?: string;
  primaryclanid?: string;
  timecreated?: number;
  personastateflags?: number;
  loccountrycode?: string;
  locstatecode?: string;
  loccityid?: number;
}

// Obtenir un token d'accès Steam (nécessite une clé API Steam)
export async function getSteamAccessToken(): Promise<string | null> {
  try {
    // TODO: Implémenter l'obtention du token d'accès Steam
    // Cela nécessite une clé API Steam et une authentification OAuth
    return null;
  } catch (error) {
    console.error('Error getting Steam access token:', error);
    return null;
  }
}

// Obtenir les jeux possédés par un utilisateur Steam
export async function getOwnedGames(steamId: string, accessToken: string): Promise<SteamGame[]> {
  try {
    const { data, error } = await supabase.functions.invoke('steam-proxy', {
      body: { action: 'getOwnedGames', steamId }
    });

    if (error) throw error;

    const response = data.response || data;
    return response.games || [];
  } catch (error) {
    console.error('Error fetching owned games from Steam:', error);
    return [];
  }
}

// Obtenir les informations d'un utilisateur Steam
export async function getPlayerSummary(steamId: string, accessToken: string): Promise<SteamPlayerSummary | null> {
  try {
    const { data, error } = await supabase.functions.invoke('steam-proxy', {
      body: { action: 'getPlayerSummaries', steamId }
    });

    if (error) throw error;

    const response = data.response || data;
    return response.players?.[0] || null;
  } catch (error) {
    console.error('Error fetching player summary from Steam:', error);
    return null;
  }
}

// Obtenir les détails d'un jeu Steam
export async function getGameDetails(appId: number): Promise<any> {
  try {
    const { data, error } = await supabase.functions.invoke('steam-proxy', {
      body: { action: 'getGameDetails', appId }
    });

    if (error) throw error;

    if (!data) return null;

    // La réponse Steam peut avoir différentes structures
    // Format 1: { "appid": { success: true, data: {...} } }
    // Format 2: { "appid": {...} }
    const appData = data[appId.toString()];

    if (!appData) {
      console.error('Steam game not found for appId:', appId);
      return null;
    }

    // Si success === false, le jeu n'existe pas ou n'est pas disponible
    if (appData.success === false) {
      console.error('Steam API returned success: false for appId:', appId);
      return null;
    }

    // Retourner les données (soit appData.data soit appData directement)
    return appData.data || appData;
  } catch (error) {
    console.error('Error fetching game details from Steam:', error);
    return null;
  }
}

// Extraire l'App ID Steam d'une URL Steam
export function extractSteamAppId(steamUrl: string): number | null {
  try {
    // Formats supportés:
    // https://store.steampowered.com/app/123456/GameName/
    // https://steamcommunity.com/app/123456/
    // steam://store/123456

    const patterns = [
      /store\.steampowered\.com\/app\/(\d+)/,
      /steamcommunity\.com\/app\/(\d+)/,
      /steam:\/\/store\/(\d+)/
    ];

    for (const pattern of patterns) {
      const match = steamUrl.match(pattern);
      if (match) {
        return parseInt(match[1], 10);
      }
    }

    return null;
  } catch (error) {
    console.error('Error extracting Steam App ID:', error);
    return null;
  }
}

// Vérifier si un utilisateur a joué à un jeu spécifique
export async function hasPlayedGame(
  steamId: string,
  appId: number,
  accessToken: string
): Promise<{ hasPlayed: boolean; playtime: number }> {
  try {
    const ownedGames = await getOwnedGames(steamId, accessToken);
    const game = ownedGames.find(g => g.appid === appId);

    return {
      hasPlayed: !!game,
      playtime: game?.playtime_forever || 0
    };
  } catch (error) {
    console.error('Error checking if user has played game:', error);
    return { hasPlayed: false, playtime: 0 };
  }
}

// Mettre à jour le temps de jeu depuis Steam pour un joueur
export async function updatePlaytimeFromSteam(
  playerId: string,
  steamId: string,
  gameId: string,
  keyRequestId: string
): Promise<boolean> {
  try {
    // Récupérer les informations du jeu depuis la base de données
    const { supabase } = await import('@/integrations/supabase/client');

    const { data: gameData, error: gameError } = await supabase
      .from('games')
      .select('steam_url, required_playtime')
      .eq('id', gameId)
      .single();

    if (gameError || !gameData?.steam_url) {
      console.error('Error fetching game data:', gameError);
      return false;
    }

    const appId = extractSteamAppId(gameData.steam_url);
    if (!appId) {
      console.error('Could not extract Steam App ID from URL:', gameData.steam_url);
      return false;
    }

    // Vérifier le temps de jeu
    const { hasPlayed, playtime } = await hasPlayedGame(steamId, appId, '');

    if (!hasPlayed) {
      return false;
    }

    const requiredPlaytime = gameData.required_playtime || 60; // 1 heure par défaut
    const playtimeRatio = Math.min(playtime / requiredPlaytime, 1);

    // Mettre à jour la base de données
    const { error: upsertError } = await supabase
      .from('playtime_tracking')
      .upsert({
        player_id: playerId,
        game_id: gameId,
        key_request_id: keyRequestId,
        steam_playtime: playtime,
        required_playtime: requiredPlaytime,
        playtime_ratio: playtimeRatio,
        last_updated: new Date().toISOString()
      });

    if (upsertError) {
      console.error('Error updating playtime tracking:', upsertError);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error updating playtime from Steam:', error);
    return false;
  }
}

// Obtenir les informations détaillées d'un utilisateur Steam (summary, level, games)
export async function getDetailedSteamInfo(steamId: string): Promise<{
  summary: SteamPlayerSummary | null;
  level: number | null;
  gameCount: number;
  totalPlaytimeHours: number;
  isPublic: boolean;
  timeCreated: number | null;
} | null> {
  try {
    const fetchAction = async (action: string) => {
      const { data, error } = await supabase.functions.invoke('steam-proxy', {
        body: { action, steamId }
      });
      if (error) throw error;
      return data;
    };

    const [summaryRes, levelRes, gamesRes] = await Promise.allSettled([
      fetchAction('getPlayerSummaries'),
      fetchAction('getSteamLevel'),
      fetchAction('getOwnedGames')
    ]);

    const summaryData = summaryRes.status === 'fulfilled' ? summaryRes.value : null;
    const levelData = levelRes.status === 'fulfilled' ? levelRes.value : null;
    const gamesData = gamesRes.status === 'fulfilled' ? gamesRes.value : null;

    const summary = summaryData?.response?.players?.[0] || null;
    const level = levelData?.response?.player_level || null;

    // Check if gamesData has the expected structure
    const gamesResponse = gamesData?.response || gamesData;
    const games = gamesResponse?.games || [];
    const gameCount = gamesResponse?.game_count || games.length;

    const totalPlaytimeMinutes = games.reduce((acc: number, game: any) => acc + (game.playtime_forever || 0), 0);
    const totalPlaytimeHours = Math.floor(totalPlaytimeMinutes / 60);
    const isPublic = summary?.communityvisibilitystate === 3;
    const timeCreated = summary?.timecreated || null;

    return {
      summary,
      level,
      gameCount,
      totalPlaytimeHours,
      isPublic,
      timeCreated
    };
  } catch (error) {
    console.error('Error fetching detailed Steam info:', error);
    return null;
  }
}

export interface SteamValidationResult {
  isValid: boolean;
  errors: string[];
}

export async function validateSteamAccount(steamId: string): Promise<SteamValidationResult> {
  const info = await getDetailedSteamInfo(steamId);
  if (!info) return { isValid: false, errors: ['Could not fetch Steam data'] };

  const errors: string[] = [];

  // Level check
  if (info.level !== null && info.level <= 3) {
    errors.push('Steam level must be greater than 3');
  } else if (info.level === null) {
    errors.push('Could not verify Steam level');
  }

  // Playtime check
  if (info.totalPlaytimeHours < 50) {
    errors.push('Total playtime must be at least 50 hours');
  }

  // Game count check
  if (info.gameCount < 5) {
    errors.push('You must own at least 5 games');
  }

  // Limited account check
  if (!info.isPublic || !info.timeCreated) {
    errors.push('Your Steam profile must be public and your account must not be limited');
  }

  // Account age check (Anti-botting: 3 months minimum)
  if (info.timeCreated) {
    const createdDate = new Date(info.timeCreated * 1000);
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
    if (createdDate > threeMonthsAgo) {
      errors.push('Steam account must be at least 3 months old');
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}
